import React, { useEffect, useState } from "react";
import {
  User,
  Shield,
  Circle,
  Hand,
  Triangle,
  Accessibility,
  ArrowRight,
  Minus,
  Square,
  Type,
  MousePointer2,
  Trash2,
  RotateCcw,
} from "lucide-react";
import { OBJECT_TYPES } from "@/constants/pitchObjects";
import { BallIcon, GoalIcon, MannequinIcon, MiniGoalIcon, CoachLetterIcon } from "./CustomIcons";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";

const ICONS = {
  User,
  Shield,
  Circle,
  Hand,
  Triangle,
  Accessibility,
  CircleDot: BallIcon,
  Goal: GoalIcon,
  Mannequin: MannequinIcon,
  MiniGoal: MiniGoalIcon,
  CoachLetter: CoachLetterIcon,
};

const PLAYER_KEYS = ["player", "defender", "neutral", "goalkeeper", "coach"];
const EQUIPMENT_KEYS = ["ball", "cone", "goal", "miniGoal", "mannequin"];
const DRAW_TOOLS = [
  { key: "arrow", label: "Arrow", Icon: ArrowRight },
  { key: "line", label: "Line", Icon: Minus },
  { key: "rect", label: "Rectangle", Icon: Square },
  { key: "circle", label: "Circle", Icon: Circle },
  { key: "text", label: "Text label", Icon: Type },
];

// Which tab does a given tool key belong to? Used to auto-switch tabs
// when a tool is activated so the pressed button remains visible.
const TOOL_TAB = {};
[...PLAYER_KEYS, ...EQUIPMENT_KEYS].forEach((k) => (TOOL_TAB[k] = "add"));
DRAW_TOOLS.forEach((t) => (TOOL_TAB[t.key] = "draw"));

const ToolButton = ({ label, active, onClick, testId, children, variant = "default", disabled = false }) => {
  const base =
    "w-11 h-11 flex items-center justify-center rounded-md transition-all duration-150 ease-in-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:ring-offset-2";
  const styles =
    variant === "danger"
      ? active
        ? "bg-red-600 text-white shadow-sm"
        : "bg-transparent text-red-600 hover:bg-red-50 hover:text-red-700 disabled:opacity-40 disabled:hover:bg-transparent"
      : active
        ? "bg-slate-900 text-white shadow-sm ring-1 ring-slate-900 ring-offset-2 ring-offset-white"
        : "bg-transparent text-slate-600 hover:bg-slate-100 hover:text-slate-900";

  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            data-testid={testId}
            onClick={disabled ? undefined : onClick}
            disabled={disabled}
            className={`${base} ${styles}`}
            aria-label={label}
            aria-pressed={active || undefined}
          >
            {children}
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="font-medium">
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const SectionLabel = ({ children }) => (
  <div className="text-[9px] tracking-[0.22em] uppercase font-bold text-slate-400 select-none">
    {children}
  </div>
);

const SectionDivider = () => (
  <div className="w-8 h-px bg-slate-200/80 my-1" role="separator" />
);

const AddObjectButton = ({ typeKey, activeTool, onSelectTool }) => {
  const def = OBJECT_TYPES[typeKey];
  const iconKey = typeKey === "mannequin" ? "Mannequin" : def.icon;
  const Icon = ICONS[iconKey] || Circle;
  // Mini Goal uses its own visually-narrower icon; the AddObjectButton
  // renders the icon slightly smaller so a coach can see at a glance
  // that this button represents the compact-size goal.
  const iconSize = typeKey === "miniGoal" ? "w-4 h-4" : "w-5 h-5";
  return (
    <ToolButton
      label={def.name}
      active={activeTool === typeKey}
      onClick={() => onSelectTool(typeKey)}
      testId={`toolbar-${typeKey}-btn`}
    >
      <Icon className={iconSize} strokeWidth={2} />
    </ToolButton>
  );
};

// Compact tab strip below Select. A tiny dot on a tab indicates that the
// currently-active tool lives in that tab but the tab isn't open.
const TabPill = ({ tabKey, activeTab, activeTool, onClick, children }) => {
  const active = activeTab === tabKey;
  const showDot = !active && TOOL_TAB[activeTool] === tabKey;
  return (
    <button
      type="button"
      onClick={() => onClick(tabKey)}
      data-testid={`toolbar-tab-${tabKey}`}
      aria-pressed={active || undefined}
      className={
        "relative px-2 py-1 text-[9px] tracking-[0.18em] uppercase font-bold rounded-sm transition-colors " +
        (active
          ? "bg-slate-900 text-white"
          : "text-slate-500 hover:text-slate-900 hover:bg-slate-100")
      }
    >
      {children}
      {showDot && (
        <span
          className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-slate-900"
          aria-hidden
        />
      )}
    </button>
  );
};

export const Toolbar = ({
  activeTool,
  onSelectTool,
  keepToolActive,
  onKeepToolActiveChange,
  onDeleteSelected,
  onClearPitch,
  hasSelection,
}) => {
  // Which tab is open — purely a UI state. Defaults to ADD. Auto-switches
  // when the coach activates a tool that lives in another tab so the
  // pressed button stays visible.
  const [activeTab, setActiveTab] = useState("add");
  useEffect(() => {
    const homeTab = TOOL_TAB[activeTool];
    if (homeTab && homeTab !== activeTab) setActiveTab(homeTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTool]);

  return (
    <aside
      className="bg-white border-r border-slate-200 flex flex-col items-center py-4 h-full overflow-y-auto"
      data-testid="left-toolbar"
    >
      {/* SELECT — permanently visible, no tab */}
      <div className="flex flex-col items-center gap-1.5" data-testid="toolbar-group-select">
        <SectionLabel>Select</SectionLabel>
        <ToolButton
          label="Select — move, resize, rotate & marquee"
          active={activeTool === "select"}
          onClick={() => onSelectTool("select")}
          testId="toolbar-select-btn"
        >
          <MousePointer2 className="w-5 h-5" strokeWidth={2} />
        </ToolButton>
      </div>

      <SectionDivider />

      {/* Tab strip */}
      <div
        className="flex flex-col items-stretch gap-0.5 w-11"
        data-testid="toolbar-tabs"
      >
        <TabPill tabKey="add"  activeTab={activeTab} activeTool={activeTool} onClick={setActiveTab}>Add</TabPill>
        <TabPill tabKey="draw" activeTab={activeTab} activeTool={activeTool} onClick={setActiveTab}>Draw</TabPill>
        <TabPill tabKey="edit" activeTab={activeTab} activeTool={activeTool} onClick={setActiveTab}>Edit</TabPill>
      </div>

      <SectionDivider />

      {/* ADD tab body */}
      {activeTab === "add" && (
        <div className="flex flex-col items-center gap-1.5 mt-1" data-testid="toolbar-group-add">
          <div
            className="flex flex-col items-center gap-1 pb-2 mb-1 border-b border-slate-200/70 w-10"
            data-testid="toolbar-keep-tool-wrap"
          >
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <label
                    htmlFor="keep-tool-active"
                    className="flex flex-col items-center gap-1 cursor-pointer select-none"
                  >
                    <Switch
                      id="keep-tool-active"
                      data-testid="keep-tool-active-toggle"
                      checked={!!keepToolActive}
                      onCheckedChange={(v) => onKeepToolActiveChange(Boolean(v))}
                      aria-label="Place Multiple"
                    />
                    <span className="text-[9px] tracking-[0.15em] uppercase font-bold text-slate-500 leading-tight text-center">
                      Place<br />Multiple
                    </span>
                  </label>
                </TooltipTrigger>
                <TooltipContent side="right" className="font-medium">
                  Place Multiple — drop several instances of the same object without re-selecting the tool
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <div className="flex flex-col items-center gap-1" data-testid="toolbar-subgroup-players">
            {PLAYER_KEYS.map((k) => (
              <AddObjectButton key={k} typeKey={k} activeTool={activeTool} onSelectTool={onSelectTool} />
            ))}
          </div>
          <div className="w-6 h-px bg-slate-200/60 my-1.5" aria-hidden />
          <div className="flex flex-col items-center gap-1" data-testid="toolbar-subgroup-equipment">
            {EQUIPMENT_KEYS.map((k) => (
              <AddObjectButton key={k} typeKey={k} activeTool={activeTool} onSelectTool={onSelectTool} />
            ))}
          </div>
        </div>
      )}

      {/* DRAW tab body */}
      {activeTab === "draw" && (
        <div className="flex flex-col items-center gap-1.5 mt-1" data-testid="toolbar-group-draw">
          <div className="flex flex-col items-center gap-1">
            {DRAW_TOOLS.map(({ key, label, Icon }) => (
              <ToolButton
                key={key}
                label={label}
                active={activeTool === key}
                onClick={() => onSelectTool(key)}
                testId={`toolbar-${key}-btn`}
              >
                <Icon className="w-5 h-5" strokeWidth={2} />
              </ToolButton>
            ))}
          </div>
        </div>
      )}

      {/* EDIT tab body */}
      {activeTab === "edit" && (
        <div className="flex flex-col items-center gap-1.5 mt-1" data-testid="toolbar-group-edit">
          <div className="flex flex-col items-center gap-1">
            <ToolButton
              label="Delete selected"
              active={false}
              onClick={hasSelection ? onDeleteSelected : undefined}
              disabled={!hasSelection}
              testId="toolbar-delete-btn"
              variant="danger"
            >
              <Trash2 className="w-5 h-5" strokeWidth={2} />
            </ToolButton>
            <ToolButton
              label="Clear pitch"
              active={false}
              onClick={onClearPitch}
              testId="toolbar-clear-btn"
              variant="danger"
            >
              <RotateCcw className="w-5 h-5" strokeWidth={2} />
            </ToolButton>
          </div>
        </div>
      )}

      <div className="flex-1" aria-hidden />
    </aside>
  );
};
